import { useState } from 'react';

interface JsonViewerProps {
  data: unknown;
  depth?: number;
  expandAll?: boolean;
}

export function JsonViewer({ data, depth = 0, expandAll = false }: JsonViewerProps) {
  const [expanded, setExpanded] = useState(expandAll || depth < 2);

  if (data === null) return <span className="text-mist/50">null</span>;
  if (data === undefined) return <span className="text-mist/50">undefined</span>;
  if (typeof data === 'boolean') return <span className="text-amber-400">{data.toString()}</span>;
  if (typeof data === 'number') return <span className="text-cyan-400">{data}</span>;
  if (typeof data === 'string') return <span className="text-success">"{data}"</span>;

  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="text-mist">[]</span>;
    return (
      <div>
        <button onClick={() => setExpanded(!expanded)} className="text-mist hover:text-parchment">
          [{expanded ? '' : `... ${data.length} items`}
        </button>
        {expanded && (
          <div className="ml-4 border-l border-stone/30 pl-2">
            {data.map((item, i) => (
              <div key={i} className="my-1">
                <span className="text-mist/50 mr-2">{i}:</span>
                <JsonViewer data={item} depth={depth + 1} expandAll={expandAll} />
              </div>
            ))}
          </div>
        )}
        {expanded && <span className="text-mist">]</span>}
      </div>
    );
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data as Record<string, unknown>);
    if (entries.length === 0) return <span className="text-mist">{'{}'}</span>;
    return (
      <div>
        <button onClick={() => setExpanded(!expanded)} className="text-mist hover:text-parchment">
          {'{'}
          {expanded ? '' : `... ${entries.length} keys`}
        </button>
        {expanded && (
          <div className="ml-4 border-l border-stone/30 pl-2">
            {entries.map(([key, value]) => (
              <div key={key} className="my-1">
                <span className="text-rune mr-1">"{key}"</span>
                <span className="text-mist mr-1">:</span>
                <JsonViewer data={value} depth={depth + 1} expandAll={expandAll} />
              </div>
            ))}
          </div>
        )}
        {expanded && <span className="text-mist">{'}'}</span>}
      </div>
    );
  }

  return <span className="text-mist">{String(data)}</span>;
}
